import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../context/TransactionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  TrendingUp, 
  TrendingDown, 
  Scale, 
  Wallet,
  Calendar,
  BarChart3,
  Award,
  Percent,
  Download,
  HelpCircle,
  Sparkles,
  Bell,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  X
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { exportPDFRecapitulatif } from '../utils/pdfExport';
import { toast } from 'sonner';
import { useSubscription } from '../context/SubscriptionContext';
import { useSettings } from '../context/SettingsContext';

// Mapping mois slug vers numéro
const MOIS_SLUGS = {
  1: 'janvier',
  2: 'fevrier', 
  3: 'mars',
  4: 'avril',
  5: 'mai',
  6: 'juin',
  7: 'juillet',
  8: 'aout',
  9: 'septembre',
  10: 'octobre',
  11: 'novembre',
  12: 'decembre'
};

// Carte KPI améliorée avec évolution N-1
// isExpense: si true, une augmentation est négative (rouge), une diminution est positive (vert)
const StatCard = ({ title, value, icon: Icon, color, bgColor, subtext, tooltip, evolution, evolutionPct, isExpense = false, formatMontant }) => {
  // Pour les dépenses, on inverse la logique des couleurs
  const getEvolutionColor = () => {
    if (evolutionPct === undefined || evolutionPct === 0) return '';
    if (isExpense) {
      // Pour les dépenses: augmentation = mauvais (rouge), diminution = bon (vert)
      return evolutionPct > 0 ? 'text-red-600' : 'text-emerald-600';
    }
    // Pour les recettes: augmentation = bon (vert), diminution = mauvais (rouge)
    return evolutionPct > 0 ? 'text-emerald-600' : 'text-red-600';
  };

  return (
    <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                {title}
              </p>
              {tooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <p className={`font-mono text-2xl font-bold tabular-nums ${color}`}>
              {typeof value === 'number' && formatMontant ? formatMontant(value) : value}
            </p>
            {/* Evolution N-1 */}
            {evolutionPct !== undefined && evolutionPct !== 0 && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${getEvolutionColor()}`}>
                {evolutionPct > 0 ? (
                  <ArrowUpRight className="h-3.5 w-3.5" />
                ) : (
                  <ArrowDownRight className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {evolutionPct > 0 ? '+' : ''}{evolutionPct.toFixed(1)}% vs N-1
                </span>
              </div>
            )}
            {subtext && (
              <p className="text-xs text-slate-400 mt-1">{subtext}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${bgColor}`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { 
    summary, 
    comparison,
    urssafNotifications,
    urssafSettings,
    selectedYear, 
    fetchSummary, 
    fetchComparison,
    fetchUrssafNotifications,
    updateUrssafSettings,
    markUrssafDeclared,
    loading 
  } = useTransactions();
  
  const { hasWatermark, trackExport, canExport, isInTrial } = useSubscription();
  const { formatMontant, formatMontantAvecSigne, getCurrencySymbol } = useSettings();
  
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    periodicite: 'trimestriel',
    notifications_actives: true,
    jours_avant_rappel: 7
  });

  useEffect(() => {
    fetchSummary(selectedYear);
    fetchComparison(selectedYear);
    fetchUrssafNotifications();
  }, [selectedYear, fetchSummary, fetchComparison, fetchUrssafNotifications]);

  useEffect(() => {
    if (urssafSettings) {
      setLocalSettings(urssafSettings);
    }
  }, [urssafSettings]);

  // Calcul de la trésorerie cumulée
  const tableData = useMemo(() => {
    if (!summary?.monthly_data) return [];
    
    let tresorerieCumul = 0;
    return summary.monthly_data.map((m, index) => {
      tresorerieCumul += m.solde;
      return {
        ...m,
        tresorerie: tresorerieCumul,
        moisNum: index + 1
      };
    });
  }, [summary]);

  // Statistiques calculées
  const stats = useMemo(() => {
    if (!summary?.monthly_data) return null;
    
    const moisAvecDonnees = summary.monthly_data.filter(m => m.total_recettes > 0 || m.total_depenses > 0);
    const nbMoisAvecDonnees = moisAvecDonnees.length || 1;
    
    // Meilleur mois (recettes)
    const meilleurMois = summary.monthly_data.reduce((best, m) => 
      m.total_recettes > (best?.total_recettes || 0) ? m : best
    , null);
    
    // Mois le plus dépensier
    const moisPlusDepensier = summary.monthly_data.reduce((worst, m) => 
      m.total_depenses > (worst?.total_depenses || 0) ? m : worst
    , null);
    
    // Moyenne mensuelle
    const moyenneRecettes = summary.total_recettes / nbMoisAvecDonnees;
    
    // Taux de marge
    const tauxMarge = summary.total_recettes > 0 
      ? ((summary.total_recettes - summary.total_depenses) / summary.total_recettes) * 100
      : 0;
    
    // Dernier mois avec données pour la trésorerie
    const dernierMoisAvecDonnees = [...moisAvecDonnees].reverse()[0];
    
    return {
      moyenneRecettes,
      meilleurMois,
      moisPlusDepensier,
      tauxMarge,
      dernierMoisAvecDonnees,
      nbMoisAvecDonnees
    };
  }, [summary]);

  // Données pour les graphiques
  const chartData = summary?.monthly_data?.map(m => ({
    name: m.mois_nom.substring(0, 3),
    recettes: m.total_recettes,
    depenses: m.total_depenses,
    solde: m.solde
  })) || [];

  const treasuryData = tableData.map(m => ({
    name: m.mois_nom.substring(0, 3),
    tresorerie: m.tresorerie
  }));

  const hasData = summary && (summary.total_recettes > 0 || summary.total_depenses > 0);

  // Navigation vers un mois
  const handleRowClick = (moisNum) => {
    const slug = MOIS_SLUGS[moisNum];
    if (slug) {
      navigate(`/app/mois/${slug}`);
    }
  };

  // Export PDF récapitulatif
  const handleExportPDF = async () => {
    // Vérifier si les données sont vides
    if (!hasData || (summary?.total_recettes === 0 && summary?.total_depenses === 0)) {
      toast.error('Aucune donnée à exporter. Veuillez d\'abord saisir des recettes ou dépenses.');
      return;
    }
    
    if (!canExport()) {
      toast.error('Limite d\'exports atteinte. Passez à Solo pour des exports illimités.');
      return;
    }
    
    try {
      const fileName = exportPDFRecapitulatif(selectedYear, summary, stats, hasWatermark());
      await trackExport(); // Incrémenter le compteur
      toast.success(`✓ PDF exporté : ${fileName}`);
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  // Sauvegarder les paramètres URSSAF
  const handleSaveSettings = async () => {
    try {
      await updateUrssafSettings(localSettings);
      setSettingsOpen(false);
      toast.success('✓ Paramètres URSSAF mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Marquer une déclaration comme effectuée
  const handleMarkDeclared = async (notificationId) => {
    try {
      await markUrssafDeclared(notificationId);
      toast.success('✓ Déclaration marquée comme effectuée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Déterminer la couleur du solde
  const getSoldeColor = (value) => {
    if (value > 0) return 'text-emerald-600';
    if (value < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  const getSoldeBgColor = (value) => {
    if (value > 0) return 'bg-emerald-50';
    if (value < 0) return 'bg-red-50';
    return 'bg-slate-50';
  };

  // Filtrer les notifications importantes
  const importantNotifications = urssafNotifications.filter(
    n => n.statut === 'en_retard' || n.statut === 'a_declarer'
  );

  return (
    <TooltipProvider>
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Notifications URSSAF */}
      {importantNotifications.length > 0 && (
        <div className="space-y-3">
          {importantNotifications.map(notif => (
            <Card 
              key={notif.id}
              className={`border-l-4 ${
                notif.statut === 'en_retard' 
                  ? 'border-l-red-500 bg-red-50' 
                  : 'border-l-amber-500 bg-amber-50'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {notif.statut === 'en_retard' ? (
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    ) : (
                      <Bell className="h-5 w-5 text-amber-600 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${notif.statut === 'en_retard' ? 'text-red-800' : 'text-amber-800'}`}>
                        {notif.message}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Chiffre d&apos;affaires à déclarer : <span className="font-mono font-semibold">{formatMontant(notif.montant_ca)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkDeclared(notif.id)}
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Déclaré
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl font-bold text-slate-900 tracking-tight">
            Récapitulatif {selectedYear}
          </h1>
          <p className="text-slate-500 mt-1">
            Vue d&apos;ensemble de votre activité
            {comparison?.recettes_precedente > 0 && (
              <span className="ml-2 text-xs text-slate-400">
                (comparaison avec {selectedYear - 1})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setSettingsOpen(true)}
            variant="outline"
            size="icon"
            className="text-slate-600"
            data-testid="urssaf-settings-btn"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button 
            onClick={handleExportPDF}
            variant="outline"
            className={`text-blue-600 border-blue-200 hover:bg-blue-50 ${(!hasData || isInTrial()) ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!hasData || isInTrial()}
            title={!hasData ? 'Aucune donnée à exporter' : isInTrial() ? 'Export PDF non disponible pendant la période d\'essai' : 'Exporter le récapitulatif en PDF'}
          data-testid="export-recap-pdf-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Exporter récapitulatif PDF
        </Button>
        </div>
      </div>

      {/* 4 Cartes KPI avec évolution N-1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Recettes"
          value={summary?.total_recettes || 0}
          icon={TrendingUp}
          color="text-emerald-600"
          bgColor="bg-emerald-50"
          subtext={stats ? `${stats.nbMoisAvecDonnees} mois avec données` : null}
          tooltip="Somme de toutes les recettes de l'année"
          evolutionPct={comparison?.recettes_evolution_pct}
          formatMontant={formatMontant}
        />
        <StatCard
          title="Total Dépenses"
          value={summary?.total_depenses || 0}
          icon={TrendingDown}
          color="text-red-600"
          bgColor="bg-red-50"
          tooltip="Somme de toutes les dépenses de l'année"
          evolutionPct={comparison?.depenses_evolution_pct}
          isExpense={true}
          formatMontant={formatMontant}
        />
        <StatCard
          title="Solde Annuel"
          value={formatMontantAvecSigne(summary?.solde || 0)}
          icon={Scale}
          color={(summary?.solde || 0) >= 0 ? "text-emerald-600" : "text-red-600"}
          bgColor={(summary?.solde || 0) >= 0 ? "bg-emerald-50" : "bg-red-50"}
          tooltip="Différence entre recettes et dépenses"
          formatMontant={formatMontant}
        />
        <StatCard
          title="Trésorerie"
          value={summary?.tresorerie || 0}
          icon={Wallet}
          color="text-blue-600"
          bgColor="bg-blue-50"
          subtext={stats?.dernierMoisAvecDonnees ? `au ${stats.dernierMoisAvecDonnees.mois_nom} ${selectedYear}` : null}
          tooltip="Cumul des soldes depuis le début de l'année"
          formatMontant={formatMontant}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Tableau récapitulatif annuel */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-200 bg-slate-50">
              <CardTitle className="font-chivo text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Récapitulatif mensuel {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800 hover:bg-slate-800">
                      <TableHead className="text-white font-semibold py-4">Mois</TableHead>
                      <TableHead className="text-white font-semibold text-right">Recettes</TableHead>
                      <TableHead className="text-white font-semibold text-right">Dépenses</TableHead>
                      <TableHead className="text-white font-semibold text-right">Solde</TableHead>
                      <TableHead className="text-white font-semibold text-right">Trésorerie</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.filter(function(row) {
                      // Ne pas afficher les mois sans données
                      return row.total_recettes > 0 || row.total_depenses > 0;
                    }).map((row, index) => {
                      return (
                        <TableRow 
                          key={row.mois}
                          className={`
                            ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}
                            hover:bg-blue-50 cursor-pointer transition-colors
                          `}
                          onClick={() => handleRowClick(row.moisNum)}
                          data-testid={`row-${row.mois_nom.toLowerCase()}`}
                        >
                          <TableCell className="font-medium py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-sm w-6">{String(row.moisNum).padStart(2, '0')}</span>
                              <span>{row.mois_nom}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">
                            {formatMontant(row.total_recettes)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600">
                            {formatMontant(row.total_depenses)}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-medium ${getSoldeColor(row.solde)}`}>
                            {formatMontantAvecSigne(row.solde)}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-medium ${getSoldeColor(row.tresorerie)}`}>
                            {formatMontant(row.tresorerie)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Message si aucune donnée */}
                    {!hasData && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          Aucune transaction enregistrée pour {selectedYear}
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Ligne Total Annuel - uniquement si des données */}
                    {hasData && (
                      <TableRow className="bg-blue-50 hover:bg-blue-100 border-t-2 border-blue-200">
                        <TableCell className="font-bold py-4 text-blue-900">
                          TOTAL ANNÉE {selectedYear}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-700 text-lg">
                          {formatMontant(summary?.total_recettes || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-red-700 text-lg">
                          {formatMontant(summary?.total_depenses || 0)}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold text-lg ${getSoldeColor(summary?.solde || 0)}`}>
                          {formatMontantAvecSigne(summary?.solde || 0)}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold text-lg ${getSoldeColor(summary?.tresorerie || 0)}`}>
                          {formatMontant(summary?.tresorerie || 0)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Graphiques */}
          {hasData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Bar Chart - Recettes vs Dépenses */}
              <Card className="lg:col-span-2 border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-chivo text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-slate-600" />
                    Recettes et Dépenses par mois
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    {chartData && chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                          <RechartsTooltip 
                            formatter={(value) => formatMontant(value)}
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Legend />
                          <Bar dataKey="recettes" name="Recettes" fill="#10b981" radius={[4, 4, 0, 0]} minPointSize={2} />
                          <Bar dataKey="depenses" name="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} minPointSize={2} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        <p>Aucune donnée à afficher</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Line Chart - Trésorerie */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-chivo text-lg">Évolution de la trésorerie</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    {treasuryData && treasuryData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <LineChart data={treasuryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                          <RechartsTooltip 
                            formatter={(value) => formatMontant(value)}
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="tresorerie" 
                            name="Trésorerie"
                            stroke="#2563eb" 
                            strokeWidth={2}
                            dot={{ fill: '#2563eb', strokeWidth: 2 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        <p>Aucune donnée à afficher</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Statistiques supplémentaires */}
          {hasData && stats && (
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="font-chivo text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-slate-600" />
                  Statistiques {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Moyenne mensuelle */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-sm font-medium">Moyenne mensuelle</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-slate-800">
                      {formatMontant(stats.moyenneRecettes)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">recettes / mois</p>
                  </div>

                  {/* Meilleur mois */}
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2">
                      <Award className="h-4 w-4" />
                      <span className="text-sm font-medium">Meilleur mois (CA)</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-emerald-700">
                      {stats.meilleurMois?.mois_nom || '-'}
                    </p>
                    <p className="text-sm text-emerald-600 mt-1">
                      {stats.meilleurMois?.total_recettes > 0 
                        ? formatMontant(stats.meilleurMois.total_recettes)
                        : '-'}
                    </p>
                  </div>

                  {/* Mois le plus dépensier */}
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <TrendingDown className="h-4 w-4" />
                      <span className="text-sm font-medium">Plus dépensier</span>
                    </div>
                    <p className="font-mono text-xl font-bold text-red-700">
                      {stats.moisPlusDepensier?.mois_nom || '-'}
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      {stats.moisPlusDepensier?.total_depenses > 0
                        ? formatMontant(stats.moisPlusDepensier.total_depenses)
                        : '-'}
                    </p>
                  </div>

                  {/* Taux de marge */}
                  <div className={`p-4 rounded-lg ${stats.tauxMarge >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
                    <div className={`flex items-center gap-2 mb-2 ${stats.tauxMarge >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                      <Percent className="h-4 w-4" />
                      <span className="text-sm font-medium">Taux de marge</span>
                    </div>
                    <p className={`font-mono text-xl font-bold ${stats.tauxMarge >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                      {stats.tauxMarge.toFixed(1)} %
                    </p>
                    <p className={`text-xs mt-1 ${stats.tauxMarge >= 0 ? 'text-blue-500' : 'text-amber-500'}`}>
                      (Recettes - Dépenses) / Recettes
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Message si pas de données */}
          {!hasData && (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="py-16 text-center">
                <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="font-chivo text-lg font-medium text-slate-900 mb-2">
                  👋 Bienvenue !
                </h3>
                <p className="text-slate-500 max-w-md mx-auto mb-4">
                  Commencez par ajouter vos premières transactions.
                  Cliquez sur un mois dans le tableau ci-dessus pour commencer.
                </p>
                <Button 
                  onClick={() => navigate('/mois/janvier')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Ajouter ma première recette
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal Paramètres URSSAF */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              Paramètres URSSAF
            </DialogTitle>
            <DialogDescription>
              Configurez vos rappels de déclaration URSSAF
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Activer/Désactiver notifications */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="notifications-active" className="text-sm font-medium">
                  Notifications actives
                </Label>
                <p className="text-xs text-slate-500">
                  Recevoir des rappels avant les échéances
                </p>
              </div>
              <Switch
                id="notifications-active"
                checked={localSettings.notifications_actives}
                onCheckedChange={(checked) => 
                  setLocalSettings({ ...localSettings, notifications_actives: checked })
                }
              />
            </div>

            {/* Périodicité */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Périodicité de déclaration</Label>
              <Select 
                value={localSettings.periodicite} 
                onValueChange={(v) => setLocalSettings({ ...localSettings, periodicite: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trimestriel">Trimestrielle</SelectItem>
                  <SelectItem value="mensuel">Mensuelle</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {localSettings.periodicite === 'trimestriel' 
                  ? 'Déclaration tous les 3 mois (T1, T2, T3, T4)'
                  : 'Déclaration chaque mois'}
              </p>
            </div>

            {/* Jours avant rappel */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Rappel avant échéance</Label>
              <Select 
                value={String(localSettings.jours_avant_rappel)} 
                onValueChange={(v) => setLocalSettings({ ...localSettings, jours_avant_rappel: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 jours avant</SelectItem>
                  <SelectItem value="7">7 jours avant</SelectItem>
                  <SelectItem value="14">14 jours avant</SelectItem>
                  <SelectItem value="30">30 jours avant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default DashboardPage;
